import { useAcceptWorkspaceInvitation } from '@hooks/postQueries';
import { CheckCircle, Error as ErrorIcon, People as PeopleIcon } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageContainer from '../../components/PageContainer';
import useSession from '../../hooks/useSession';

const WorkspaceAcceptInvitation = () => {
  const { workspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false);

  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'already_member'
  const [error, setError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [workspaceName, setWorkspaceName] = useState('');
  const [hasAttemptedAcceptance, setHasAttemptedAcceptance] = useState(false);
  const { isAuthenticated, loading: sessionLoading } = useSession();
  const acceptInvitationMutation = useAcceptWorkspaceInvitation();

  // Handle authentication check
  useEffect(() => {
    if (sessionLoading) {
      return;
    }

    if (!isAuthenticated) {
      const currentUrl = window.location.href;
      localStorage.setItem('acceptShareRequestUrl', currentUrl);
      navigate('/signin');
      return;
    }

    if (!workspaceId || !token) {
      setStatus('error');
      setError('Invalid invitation link.');
      return;
    }
  }, [sessionLoading, isAuthenticated, workspaceId, token, navigate]);

  // Handle invitation acceptance
  useEffect(() => {
    if (
      sessionLoading ||
      !isAuthenticated ||
      hasAttemptedAcceptance ||
      !workspaceId ||
      !token ||
      status !== 'loading'
    ) {
      return;
    }

    setHasAttemptedAcceptance(true);
    acceptInvitationMutation.mutate(
      { workspaceId, token },
      {
        onSuccess: () => {
          console.log('Invitation accepted successfully, reloading for workspace:', workspaceId);
          localStorage.setItem('activeWorkspaceId', workspaceId);
          localStorage.removeItem('acceptShareRequestUrl');
          // Perform a full page reload to ensure workspace headers and state are correctly initialized
          window.location.assign(`/workspaces/${workspaceId}`);
        },
        onError: (error) => {
          localStorage.removeItem('acceptShareRequestUrl');
          if (error?.detail?.includes('already a member') || error?.status === 409) {
            setStatus('already_member');
          } else {
            setStatus('error');
            setError(
              error?.detail || 'Failed to accept invitation. Please try again or contact support.',
            );
          }
        },
      },
    );
  }, [sessionLoading, isAuthenticated, workspaceId, token]); // Removed unnecessary dependencies

  // Clean up URL parameters when component unmounts to prevent navigation issues
  useEffect(() => {
    return () => {
      // Cleanup function to ensure URL is clean when leaving this page
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
  }, []);

  const handleGoToWorkspace = () => {
    setIsWorkspaceLoading(true);
    localStorage.setItem('activeWorkspaceId', workspaceId);
    setTimeout(() => {
      window.open(`/dashboard`, '_self');
      setIsWorkspaceLoading(false);
    }, 1000);
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Processing your invitation...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we add you to the workspace.
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'primary.main',
                mx: 'auto',
                mb: 2,
              }}
            >
              <CheckCircle sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" gutterBottom color="primary.main">
              Welcome to {workspaceName}!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You&apos;ve successfully joined the workspace. You can now collaborate with your team
              members.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={isWorkspaceLoading}
                startIcon={<PeopleIcon />}
                onClick={handleGoToWorkspace}
                size="large"
              >
                {isWorkspaceLoading ? 'Loading...' : 'Go to Workspace'}
              </Button>
            </Box>
          </Box>
        );

      case 'already_member':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'info.main',
                mx: 'auto',
                mb: 2,
              }}
            >
              <PeopleIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" gutterBottom color="info.main">
              Already a Member
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<PeopleIcon />}
                onClick={handleGoToWorkspace}
                size="large"
              >
                Go to Workspace
              </Button>
            </Box>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'error.main',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ErrorIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" gutterBottom color="error.main">
              Invitation Error
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">This could happen if:</Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>The invitation link has expired</li>
                <li>The invitation has already been used</li>
                <li>The invitation has been revoked</li>
                <li>There&apos;s a network connectivity issue</li>
              </ul>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer title="Accept Workspace Invitation" description="Join your team workspace">
      <Box
        sx={{
          backgroundColor: 'grey.100',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card
          variant="outlined"
          sx={{
            maxWidth: 600,
            width: '100%',
            mx: 2,
          }}
        >
          <CardContent>{renderContent()}</CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default WorkspaceAcceptInvitation;
